import React from 'react';

export default function PrivacyPolicy() {
  return (
    <>
      {/* ===== STYLES ===== */}
      <style>{`
        /* ========================================
           PRIVACY POLICY - PREMIUM DESIGN
           ======================================== */

        /* ===== IMPORT GOOGLE FONTS ===== */
        @import url('https://fonts.googleapis.com/css2?family=Inter:wght@300;400;500;600;700;800&display=swap');

        /* ===== ROOT VARIABLES ===== */
        :root {
          --primary: #6C3CE1;
          --primary-light: #8B5CF6;
          --primary-dark: #5B21B6;
          --primary-gradient: linear-gradient(135deg, #6C3CE1 0%, #A78BFA 100%);
          --text-primary: #1A1A2E;
          --text-secondary: #4A4A6A;
          --bg-white: #FFFFFF;
          --bg-light: #F8F7FF;
          --border-color: #E8E5F5;
          --shadow-lg: 0 20px 60px rgba(108, 60, 225, 0.15);
          --radius: 20px;
          --radius-sm: 12px;
          --transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);
        }

        /* ===== DARK MODE ===== */
        @media (prefers-color-scheme: dark) {
          :root {
            --bg-white: #0A0A1A;
            --bg-light: #111128;
            --text-primary: #E8E8F8;
            --text-secondary: #B8B8D8;
            --border-color: #2A2A5A;
            --shadow-lg: 0 20px 60px rgba(0, 0, 0, 0.6);
          }
        }

        /* ===== MAIN CONTAINER ===== */
        .pp-container {
          max-width: 920px;
          margin: 2rem auto;
          padding: 0;
          font-family: 'Inter', -apple-system, BlinkMacSystemFont, sans-serif;
          color: var(--text-primary);
          background: transparent;
        }

        /* ===== HERO SECTION ===== */
        .pp-hero {
          background: var(--primary-gradient);
          padding: 3.5rem 3rem;
          border-radius: var(--radius) var(--radius) 0 0;
          position: relative;
          overflow: hidden;
        }

        .pp-hero::after {
          content: '🔒';
          position: absolute;
          bottom: 1.5rem;
          right: 2.5rem;
          font-size: 4rem;
          opacity: 0.15;
          pointer-events: none;
        }

        .pp-hero .badge {
          display: inline-block;
          background: rgba(255, 255, 255, 0.2);
          backdrop-filter: blur(10px);
          padding: 0.3rem 1.2rem;
          border-radius: 50px;
          font-size: 0.75rem;
          font-weight: 600;
          color: #FFFFFF;
          text-transform: uppercase;
          letter-spacing: 1.5px;
          margin-bottom: 1rem;
        }

        .pp-hero h1 {
          font-size: 2.8rem;
          font-weight: 800;
          color: #FFFFFF;
          margin: 0;
          letter-spacing: -0.03em;
          line-height: 1.2;
        }

        .pp-hero p {
          color: rgba(255, 255, 255, 0.85);
          font-size: 1.1rem;
          margin-top: 0.5rem;
          margin-bottom: 0;
          font-weight: 400;
          max-width: 80%;
        }

        .pp-hero .update-date {
          display: inline-block;
          margin-top: 1rem;
          background: rgba(255, 255, 255, 0.15);
          padding: 0.4rem 1.2rem;
          border-radius: 50px;
          font-size: 0.8rem;
          color: rgba(255, 255, 255, 0.9);
          backdrop-filter: blur(10px);
        }

        /* ===== CONTENT BODY ===== */
        .pp-body {
          background: var(--bg-white);
          padding: 2.5rem 3rem;
          border-radius: 0 0 var(--radius) var(--radius);
          box-shadow: var(--shadow-lg);
          border: 1px solid var(--border-color);
          border-top: none;
        }

        /* ===== HEADINGS ===== */
        .pp-body h2 {
          font-size: 1.6rem;
          font-weight: 700;
          color: var(--text-primary);
          margin-top: 2.5rem;
          margin-bottom: 1.2rem;
          padding-bottom: 0.6rem;
          border-bottom: 2px solid var(--border-color);
          display: flex;
          align-items: center;
          gap: 0.75rem;
        }

        .pp-body h2:first-of-type {
          margin-top: 0;
        }

        .pp-body h2 .icon {
          display: inline-flex;
          align-items: center;
          justify-content: center;
          background: var(--primary-gradient);
          color: #FFFFFF;
          width: 2rem;
          height: 2rem;
          border-radius: 50%;
          font-size: 1rem;
          flex-shrink: 0;
        }

        .pp-body h3 {
          font-size: 1.2rem;
          font-weight: 600;
          color: var(--text-primary);
          margin-top: 1.8rem;
          margin-bottom: 0.8rem;
          display: flex;
          align-items: center;
          gap: 0.5rem;
        }

        .pp-body h3 .dot {
          display: inline-block;
          width: 6px;
          height: 6px;
          background: var(--primary-gradient);
          border-radius: 50%;
          flex-shrink: 0;
        }

        /* ===== PARAGRAPHS ===== */
        .pp-body p {
          font-size: 1rem;
          line-height: 1.8;
          color: var(--text-secondary);
          margin-bottom: 1.2rem;
        }

        /* ===== LISTS ===== */
        .pp-body ul {
          list-style: none;
          padding: 0;
          margin: 1.2rem 0 1.8rem 0;
          display: grid;
          grid-template-columns: 1fr;
          gap: 0.6rem;
        }

        .pp-body ul li {
          padding: 0.8rem 1.2rem 0.8rem 2.8rem;
          background: var(--bg-light);
          border-radius: var(--radius-sm);
          color: var(--text-secondary);
          font-size: 0.95rem;
          line-height: 1.6;
          position: relative;
          transition: var(--transition);
          border: 1px solid transparent;
        }

        .pp-body ul li:hover {
          transform: translateX(6px);
          border-color: var(--primary-light);
          box-shadow: 0 2px 8px rgba(108, 60, 225, 0.1);
        }

        .pp-body ul li::before {
          content: '✓';
          position: absolute;
          left: 0.8rem;
          top: 50%;
          transform: translateY(-50%);
          background: var(--primary-gradient);
          color: #FFFFFF;
          width: 1.5rem;
          height: 1.5rem;
          border-radius: 50%;
          display: flex;
          align-items: center;
          justify-content: center;
          font-size: 0.75rem;
          font-weight: 700;
        }

        /* ===== HIGHLIGHT BOX ===== */
        .pp-body .highlight-box {
          background: linear-gradient(135deg, #F8F7FF 0%, #EEEAFF 100%);
          border-left: 4px solid var(--primary);
          border-radius: var(--radius-sm);
          padding: 1.5rem 2rem;
          margin: 1.5rem 0;
        }

        @media (prefers-color-scheme: dark) {
          .pp-body .highlight-box {
            background: linear-gradient(135deg, #151538 0%, #1A1A4A 100%);
          }
        }

        .pp-body .highlight-box p {
          margin-bottom: 0;
          color: var(--text-primary);
        }

        /* ===== CONTACT BOX ===== */
        .pp-body .contact-box {
          background: var(--bg-white);
          border: 2px solid var(--border-color);
          border-radius: var(--radius-sm);
          padding: 2rem 2.5rem;
          margin-top: 2rem;
          transition: var(--transition);
          position: relative;
          overflow: hidden;
        }

        .pp-body .contact-box::before {
          content: '';
          position: absolute;
          top: 0;
          left: 0;
          right: 0;
          height: 4px;
          background: var(--primary-gradient);
        }

        .pp-body .contact-box:hover {
          border-color: var(--primary-light);
          box-shadow: 0 8px 30px rgba(108, 60, 225, 0.12);
          transform: translateY(-2px);
        }

        .pp-body .contact-box .contact-item {
          display: flex;
          align-items: center;
          gap: 1rem;
          padding: 0.6rem 0;
          border-bottom: 1px solid var(--border-color);
        }

        .pp-body .contact-box .contact-item:last-child {
          border-bottom: none;
        }

        .pp-body .contact-box .contact-item .label {
          font-weight: 600;
          color: var(--text-primary);
          min-width: 90px;
          font-size: 0.9rem;
        }

        .pp-body .contact-box .contact-item .value {
          color: var(--text-secondary);
          font-size: 0.95rem;
        }

        .pp-body .contact-box .contact-item .value a {
          color: var(--primary);
          text-decoration: none;
          font-weight: 500;
          transition: var(--transition);
        }

        .pp-body .contact-box .contact-item .value a:hover {
          color: var(--primary-dark);
          text-decoration: underline;
        }

        /* ===== BACK TO TOP ===== */
        .pp-body .back-to-top {
          display: inline-flex;
          align-items: center;
          gap: 0.5rem;
          margin-top: 2.5rem;
          padding: 0.7rem 1.5rem;
          background: var(--bg-light);
          border: 1px solid var(--border-color);
          border-radius: 50px;
          color: var(--text-secondary);
          font-size: 0.85rem;
          font-weight: 500;
          cursor: pointer;
          transition: var(--transition);
          text-decoration: none;
        }

        .pp-body .back-to-top:hover {
          background: var(--primary);
          color: #FFFFFF;
          border-color: var(--primary);
          transform: translateY(-2px);
          box-shadow: 0 2px 8px rgba(108, 60, 225, 0.2);
        }

        /* ===== RESPONSIVE ===== */
        @media (max-width: 768px) {
          .pp-hero {
            padding: 2.5rem 1.8rem;
          }
          .pp-hero h1 {
            font-size: 2rem;
          }
          .pp-hero p {
            max-width: 100%;
            font-size: 1rem;
          }
          .pp-hero::after {
            font-size: 3rem;
            bottom: 1rem;
            right: 1.5rem;
          }
          .pp-body {
            padding: 1.8rem;
          }
          .pp-body h2 {
            font-size: 1.3rem;
          }
          .pp-body h3 {
            font-size: 1.05rem;
          }
          .pp-body ul li {
            font-size: 0.9rem;
            padding: 0.6rem 1rem 0.6rem 2.5rem;
          }
          .pp-body .contact-box {
            padding: 1.5rem;
          }
          .pp-body .contact-box .contact-item {
            flex-direction: column;
            align-items: flex-start;
            gap: 0.2rem;
          }
          .pp-body .contact-box .contact-item .label {
            min-width: auto;
          }
        }

        @media (max-width: 480px) {
          .pp-container {
            margin: 0.5rem;
          }
          .pp-hero {
            padding: 2rem 1.2rem;
            border-radius: var(--radius-sm) var(--radius-sm) 0 0;
          }
          .pp-hero h1 {
            font-size: 1.6rem;
          }
          .pp-hero .badge {
            font-size: 0.6rem;
            padding: 0.2rem 0.8rem;
          }
          .pp-hero .update-date {
            font-size: 0.7rem;
          }
          .pp-hero::after {
            display: none;
          }
          .pp-body {
            padding: 1.2rem;
            border-radius: 0 0 var(--radius-sm) var(--radius-sm);
          }
          .pp-body h2 {
            font-size: 1.1rem;
            margin-top: 1.8rem;
          }
          .pp-body h2 .icon {
            width: 1.6rem;
            height: 1.6rem;
            font-size: 0.8rem;
          }
          .pp-body h3 {
            font-size: 0.95rem;
          }
          .pp-body p {
            font-size: 0.9rem;
          }
          .pp-body ul {
            gap: 0.4rem;
          }
          .pp-body ul li {
            font-size: 0.85rem;
            padding: 0.5rem 0.8rem 0.5rem 2.2rem;
            border-radius: 8px;
          }
          .pp-body ul li::before {
            width: 1.2rem;
            height: 1.2rem;
            font-size: 0.65rem;
            left: 0.6rem;
          }
          .pp-body .contact-box {
            padding: 1rem;
          }
          .pp-body .contact-box .contact-item .label {
            font-size: 0.8rem;
          }
          .pp-body .contact-box .contact-item .value {
            font-size: 0.85rem;
          }
          .pp-body .back-to-top {
            font-size: 0.75rem;
            padding: 0.5rem 1rem;
          }
        }

        /* ===== PRINT ===== */
        @media print {
          .pp-container {
            margin: 0;
            box-shadow: none;
          }
          .pp-hero {
            background: #6C3CE1 !important;
            -webkit-print-color-adjust: exact !important;
            print-color-adjust: exact !important;
          }
          .pp-body {
            box-shadow: none;
            border: 1px solid #ddd;
          }
          .pp-body ul li {
            background: #f5f5f5 !important;
            -webkit-print-color-adjust: exact !important;
            print-color-adjust: exact !important;
          }
          .pp-body ul li::before {
            background: #6C3CE1 !important;
          }
          .pp-body .contact-box {
            border: 1px solid #ccc;
            background: #f9f9f9 !important;
            -webkit-print-color-adjust: exact !important;
            print-color-adjust: exact !important;
          }
          .pp-body .contact-box::before {
            background: #6C3CE1 !important;
          }
        }
      `}</style>

      {/* ===== HTML CONTENT ===== */}
      <div className="pp-container">
        
        {/* HERO */}
        <div className="pp-hero">
          <span className="badge">🔐 Privacy & Security</span>
          <h1>Privacy Policy</h1>
          <p>Your privacy matters to us. Learn how we collect, use, and protect your data on MK Shops.</p>
          <span className="update-date">📅 Last Updated: January 2026</span>
        </div>

        {/* BODY */}
        <div className="pp-body">

          {/* Section 1 */}
          <h2><span className="icon">📋</span> 1. Information We Collect</h2>
          <p>MK Shops allows visitors to browse products without creating an account. We only collect personal information when it is necessary to provide specific features and services.</p>

          <h3><span className="dot"></span> Customer Reviews</h3>
          <p>To submit a product review, customers are required to create an account. During registration, we collect:</p>
          <ul>
            <li>Full Name</li>
            <li>Email Address</li>
          </ul>
          <p>An OTP (One-Time Password) is sent to the provided email address for verification before the account is created.</p>

          <h3><span className="dot"></span> Vendor Registration</h3>
          <p>Vendors are required to provide the following information:</p>
          <ul>
            <li>Shop Name</li>
            <li>Email Address</li>
          </ul>
          <p>Vendors may also choose to provide the following optional information:</p>
          <ul>
            <li>Shop Description (Optional)</li>
            <li>Phone Number (Optional)</li>
            <li>Shop Address (Optional)</li>
          </ul>
          <p>We may also collect information related to the products uploaded by vendors to their stores.</p>

          {/* Section 2 */}
          <h2><span className="icon">⚙️</span> 2. How We Use Your Information</h2>
          <p>We use the information we collect to:</p>
          <ul>
            <li>Verify your email address using OTP.</li>
            <li>Create and manage customer and vendor accounts.</li>
            <li>Allow customers to submit product reviews.</li>
            <li>Manage vendor stores and product listings.</li>
            <li>Improve our website and user experience.</li>
            <li>Prevent fraudulent or unauthorized activities.</li>
            <li>Respond to customer support requests.</li>
          </ul>

          {/* Section 3 */}
          <h2><span className="icon">✅</span> 3. Vendor Profiles and Verification Badges</h2>
          <p>MK Shops may display verification badges on vendor profiles to help customers identify trusted stores.</p>
          <ul>
            <li>Vendors who complete their profile may receive a verification badge according to our verification process.</li>
            <li>Optional information such as the shop description, phone number, and address may be displayed publicly if the vendor chooses to provide it.</li>
            <li>Vendor badges and ratings may change over time based on profile completion, customer reviews, and overall store performance.</li>
            <li>MK Shops reserves the right to update, remove, or modify verification badges at any time.</li>
          </ul>

          {/* Section 4 - Cookies */}
          <h2><span className="icon">🍪</span> 4. Cookies</h2>
          <div className="highlight-box">
            <p><strong>✨ Did you know?</strong> Cookies help us remember your preferences and make your shopping experience smoother and more personalized.</p>
          </div>
          <p>MK Shops uses cookies and similar technologies to improve your browsing experience, enhance website functionality, and provide a better user experience.</p>
          <p>Cookies may be used for the following purposes:</p>
          <ul>
            <li>Remember your preferences and settings.</li>
            <li>Maintain secure user sessions.</li>
            <li>Improve website performance.</li>
            <li>Analyze website traffic and usage.</li>
            <li>Detect and prevent fraudulent activities.</li>
            <li>Provide personalized advertisements through Google AdSense (when applicable).</li>
          </ul>
          <p>Most web browsers allow you to control or disable cookies through their settings. Disabling cookies may affect certain features and functionality of the website.</p>
          <p>By continuing to use MK Shops, you consent to the use of cookies in accordance with this Privacy Policy.</p>

          {/* Section 6 */}
          <h2><span className="icon">🔗</span> 6. Third-Party Services</h2>
          <p>MK Shops may use trusted third-party services to provide and improve our platform. These services may process certain information as required to perform their functions.</p>
          <ul>
            <li>Google AdSense for displaying advertisements.</li>
            <li>Email services for OTP verification and account notifications.</li>
            <li>Cloud storage services for storing product and shop images.</li>
            <li>Hosting providers for website and server infrastructure.</li>
          </ul>
          <p>Each third-party service operates under its own privacy policy. We encourage users to review those policies before using our services.</p>

          {/* Section 10 */}
          <h2><span className="icon">📝</span> 10. Changes to This Privacy Policy</h2>
          <p>MK Shops may update this Privacy Policy from time to time to reflect changes in our services, legal requirements, or business practices.</p>
          <p>Any updates will be posted on this page with a revised "Effective Date." We encourage users to review this Privacy Policy periodically to stay informed about how we protect their information.</p>

          {/* Section 11 - Contact */}
          <h2><span className="icon">📬</span> 11. Contact Us</h2>
          <p>If you have any questions, concerns, or requests regarding this Privacy Policy, please feel free to contact us.</p>

          <div className="contact-box">
            <div className="contact-item">
              <span className="label">🌐 Website</span>
              <span className="value">MK Shops</span>
            </div>
            <div className="contact-item">
              <span className="label">📧 Email</span>
              <span className="value"><a href="mailto:mktorushops@gmail.com">mktorushops@gmail.com</a></span>
            </div>
            <div className="contact-item">
              <span className="label">💬 Support</span>
              <span className="value">We'll respond to your inquiry as soon as possible</span>
            </div>
          </div>

          {/* Back to Top */}
          <a href="#" className="back-to-top">↑ Back to Top</a>

        </div>
      </div>
    </>
  );
}